import {
  AppShellMain,
  AppShell as BaseAppShell,
  Center,
  Stack,
} from "@mantine/core"
import {
  IconAdjustments,
  IconFileDollar,
  IconLogout,
  IconLanguage,
  IconUserEdit,
} from "@tabler/icons-react"
import {
  AppNavBar,
  IconDashboard,
  IconRegulations,
  IconProducts,
  IconSuppliers,
  IconCertificates,
  IconDataTransfer,
  IconFAQ,
  MenuItem,
  IconCommunicationStudio,
  UserMenu,
  AppLanguageMenu,
  IconFavicon,
  NavBarItem,
} from "@tfg/ui"
import i18n from "i18next"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Outlet, useNavigate } from "react-router-dom"

import { LanguageIso6391 } from "@tfg/types/constants/language.constants"
import { OrganizationType } from "@tfg/types/types/organization.types"

import { BRAND_LOCALES } from "~/i18n"
import { useAuth } from "~/store/auth-context.tsx"
import { findLanguage, findLanguageFlag } from "~/utils/languages"

export const useGetNavMenuItems = (type: "brand" | "supplier") => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  if (type === "brand")
    return [
      {
        onClick: () => navigate("/"),
        label: t("menus.dashboard"),
        Icon: IconDashboard,
        isActive: window.location.pathname === "/",
      },
      {
        onClick: () => navigate("/regulations"),
        label: t("menus.regulations"),
        Icon: IconRegulations,
        isActive: window.location.pathname.startsWith("/regulations"),
      },
      {
        onClick: () => navigate("/products"),
        label: t("menus.products"),
        Icon: IconProducts,
        isActive: window.location.pathname.startsWith("/products"),
      },
      {
        onClick: () => navigate("/suppliers"),
        label: t("menus.businessPartners"),
        Icon: IconSuppliers,
        isActive: window.location.pathname.startsWith("/suppliers"),
      },
      {
        onClick: () => navigate("/documents"),
        label: t("menus.certificates"),
        Icon: IconCertificates,
        isActive: window.location.pathname.startsWith("/documents"),
      },
      {
        onClick: () => navigate("/data-transfer"),
        label: t("menus.dataTransfer"),
        Icon: IconDataTransfer,
        isActive: window.location.pathname.startsWith("/data-transfer"),
      },
      {
        onClick: () => navigate("/communication-studio"),
        label: t("menus.communicationStudio"),
        Icon: IconCommunicationStudio,
        isActive: window.location.pathname.startsWith("/communication-studio"),
      },
    ]
  if (type === "supplier")
    return [
      {
        onClick: () => navigate("/"),
        label: t("menus.dashboard"),
        Icon: IconDashboard,
        isActive: window.location.pathname === "/",
      },
    ]
  return []
}

export const useGetSettingsMenuItems = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  return [
    {
      onClick: () => navigate("/faq"),
      label: t("menus.faq"),
      Icon: IconFAQ,
      isActive: window.location.pathname.startsWith("/faq"),
    },
  ]
}

export const useGetSupplierMenuItems = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  return [
    {
      onClick: () => navigate("/"),
      label: t("menus.dashboard"),
      Icon: IconDashboard,
      isActive: window.location.pathname === "/",
    },
  ]
}

export const BrandAppShell = () => {
  const { t } = useTranslation()
  const { user, logout, isAdmin } = useAuth()
  const isClient =
    user?.organization.type === OrganizationType.BRAND ||
    user?.organization.type === OrganizationType.PLATFORM_ADMIN
  const navigate = useNavigate()
  const [currentLocale, setCurrentLocale] = useState<string>(i18n.language)
  const navItems = useGetNavMenuItems(isClient ? "brand" : "supplier")
  const USER_MENU_ITEMS = [
    {
      label: t("menus.profile"),
      icon: <IconUserEdit size="1rem" color="var(--tag-color-info)" />,
      isActive: window.location.pathname.startsWith("/profile"),
      onClick: () => {
        navigate("/profile")
      },
    },
    {
      label: t("menus.invoices"),
      icon: <IconFileDollar size="1rem" color="var(--tag-color-info)" />,
      isActive: window.location.pathname.startsWith("/invoices"),
      onClick: () => {
        navigate("/invoices")
      },
    },
    {
      label: t("menus.logout"),
      icon: <IconLogout size="1rem" color="var(--tag-color-info)" />,
      isActive: false,
      onClick: () => {
        logout()
      },
    },
  ]

  const adminItem: MenuItem | null = isAdmin
    ? {
        label: t("menus.admin"),
        icon: <IconAdjustments size="1rem" color="var(--tag-color-info)" />,
        onClick: () => navigate("/admin"),
        isActive: window.location.pathname.startsWith("/admin"),
      }
    : null

  const menuItems = adminItem
    ? [
        ...USER_MENU_ITEMS.slice(0, -1),
        adminItem,
        USER_MENU_ITEMS[USER_MENU_ITEMS.length - 1],
      ]
    : USER_MENU_ITEMS

  const SETTINGS_MENU_ITEMS = useGetSettingsMenuItems()
  if (!user) return null
  return (
    <BaseAppShell
      padding={0}
      layout="alt"
      navbar={{ width: "var(--mantine-brand-navbar-width)", breakpoint: 0 }}
    >
      <AppNavBar
        navItems={navItems}
        logo={
          <Center h="var(--navbar-item-height)">
            <IconFavicon />
          </Center>
        }
        footerContent={
          <Stack
            pos="relative"
            w="100%"
            justify="center"
            align="center"
            gap={0}
          >
            {SETTINGS_MENU_ITEMS.map((item, index) => (
              <NavBarItem item={item} key={`${item.label}-${index}`} />
            ))}
            <AppLanguageMenu
              variant="dark"
              currentLocale={currentLocale.toUpperCase()}
              handleLanguageChange={async (locale) => {
                await i18n.changeLanguage(locale)
                setCurrentLocale(locale)
              }}
              dropDownPosition="right"
              trigger={
                <Stack
                  w="100%"
                  justify="center"
                  align="center"
                  h="var(--navbar-item-height)"
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor =
                      "var(--mantine-color-primary-4)"
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent"
                  }}
                >
                  <IconLanguage size={24} color="white" />
                </Stack>
              }
              languageItems={BRAND_LOCALES.map((locale) => ({
                label: findLanguage(locale.toUpperCase()),
                value: locale,
                icon: findLanguageFlag(locale.toUpperCase() as LanguageIso6391),
              }))}
            />
            <Center mih="var(--navbar-item-height)">
              <UserMenu
                user={user}
                menuItems={menuItems}
                withName={false}
                withAvatar={true}
                dropdownPosition="right"
                avatarProps={{
                  color: "white",
                  c: "var(--mantine-color-text)",
                }}
              />
            </Center>
          </Stack>
        }
      />
      <AppShellMain>
        <Outlet />
      </AppShellMain>
    </BaseAppShell>
  )
}
